import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';
import { Fragment, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../../shared/lib/cn';

import {
  BATCH_ATTENDANCE_QUERY_KEYS,
  clearDailyAttendance,
  type DailyAttendanceStatus,
  markDailyAttendance,
  type MonthlyAttendanceDay,
  type MonthlyAttendanceMark,
  type MonthlyAttendanceStudent,
  type MonthlyAttendanceView,
} from './api';
import {
  type DayMeta,
  indexDays,
  indexMarks,
  initialsFor,
  type MarkInfo,
  totalsFor,
} from './attendance-range';
import s from './AttendanceMatrix.module.css';

/**
 * One batch's slice of the matrix. Every group carries its own day metadata
 * because the workday mask and holidays are per-batch: in "All batches" mode a
 * Saturday can be a class day for one row and shaded off for the next.
 */
export interface MatrixGroup {
  batchId: string;
  batchName: string;
  days: MonthlyAttendanceDay[];
  students: MonthlyAttendanceStudent[];
  marks: MonthlyAttendanceMark[];
}

interface Props {
  /** Visible dates, ascending. Totals and the footer are windowed to these. */
  dates: readonly string[];
  groups: readonly MatrixGroup[];
  /** Band the rows under a per-batch header — used by "All batches". */
  showGroupHeaders?: boolean;
  /** `compact` narrows the day columns so a whole month fits without scrolling. */
  density?: 'comfortable' | 'compact';
  rangeLabel: string;
  eyebrow?: string;
  onPrev?: () => void;
  onNext?: () => void;
  onJumpToCurrent?: () => void;
  canJumpToCurrent?: boolean;
  nextDisabled?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
}

const STATUS_GLYPH: Record<DailyAttendanceStatus, string> = {
  present: 'P',
  absent: 'A',
  late: 'L',
  excused: 'E',
};

const CHIP_CLASS: Record<DailyAttendanceStatus, string> = {
  present: s.chipPresent ?? '',
  absent: s.chipAbsent ?? '',
  late: s.chipLate ?? '',
  excused: s.chipExcused ?? '',
};

const STATUS_LABEL: Record<DailyAttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  excused: 'Excused',
};

/**
 * Click order. Unlike the original four-status ring this returns to `null`,
 * so a mis-click is always four clicks from undone rather than permanent.
 * Erasing goes through `clear_daily_attendance`, which needs the Phase 130
 * migration — until it is applied the last hop reports why it failed.
 */
const CYCLE: Record<DailyAttendanceStatus, DailyAttendanceStatus | null> = {
  present: 'absent',
  absent: 'late',
  late: 'excused',
  excused: null,
};

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * `autoMarked` short-circuits the cycle for a cell the nightly auto-absent
 * job defaulted (Phase 135) rather than a person: the first click jumps
 * straight to Present instead of continuing on to Late. That is the common
 * correction — "the system just hadn't been told yet" — and it is also what
 * a blank cell already did before this cell had ever been touched, so the
 * one-click muscle memory does not change for the case teachers hit most.
 * A cell a person actually marked Absent is unaffected and keeps the normal
 * absent -> late progression.
 */
export function nextStatus(
  current: DailyAttendanceStatus | null,
  autoMarked = false,
): DailyAttendanceStatus | null {
  if (current === null) return 'present';
  if (current === 'absent' && autoMarked) return 'present';
  // eslint-disable-next-line security/detect-object-injection -- enum key
  return CYCLE[current];
}

function ringStrokeClass(pct: number): string {
  if (pct >= 90) return s.ringFillSuccess ?? '';
  if (pct < 70) return s.ringFillWarn ?? '';
  return s.ringFillBrand ?? '';
}

interface MutationVars {
  batchId: string;
  studentId: string;
  date: string;
  next: DailyAttendanceStatus | null;
}

interface MutationContext {
  queryKey: readonly unknown[];
  previous: MonthlyAttendanceView | undefined;
}

/** The month payload a given cell lives in — the only cache the matrix writes. */
function monthKeyFor(batchId: string, date: string): readonly unknown[] {
  const d = dayjs(date);
  return BATCH_ATTENDANCE_QUERY_KEYS.month(batchId, d.year(), d.month() + 1);
}

interface DayTally {
  date: string;
  /** Present + late across every visible row. */
  attended: number;
  marked: number;
  /** Rows for which this date is a class day. */
  roster: number;
  anyWorkday: boolean;
  /** The class hasn't met yet — nothing here is markable or countable. */
  isFuture: boolean;
}

interface FlatRow {
  group: MatrixGroup;
  student: MonthlyAttendanceStudent;
  dayMeta: ReadonlyMap<string, DayMeta>;
  marks: ReadonlyMap<string, MarkInfo> | undefined;
  /** Index of this row's group, so a band can be emitted before its first row. */
  groupIndex: number;
  firstOfGroup: boolean;
}

export function AttendanceMatrix({
  dates,
  groups,
  showGroupHeaders = false,
  density = 'comfortable',
  rangeLabel,
  eyebrow = 'Attendance',
  onPrev,
  onNext,
  onJumpToCurrent,
  canJumpToCurrent,
  nextDisabled,
  emptyTitle = 'No students in this batch yet',
  emptyBody = 'Assign students from the Batches page to start tracking attendance.',
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const [expanded, setExpanded] = useState(false);
  const [focusKey, setFocusKey] = useState<{ r: number; d: number }>({ r: 0, d: 0 });
  const [pendingCells, setPendingCells] = useState<ReadonlySet<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);

  // Lock the body scroll while the expanded overlay is open + close on Esc.
  useEffect(() => {
    if (!expanded) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  const rows = useMemo<FlatRow[]>(() => {
    const out: FlatRow[] = [];
    groups.forEach((group, groupIndex) => {
      const dayMeta = indexDays(group.days);
      const byStudent = indexMarks(group.marks);
      group.students.forEach((student, i) => {
        out.push({
          group,
          student,
          dayMeta,
          marks: byStudent.get(student.student_id),
          groupIndex,
          firstOfGroup: i === 0,
        });
      });
    });
    return out;
  }, [groups]);

  // Per-day tally across every visible row — the strip along the bottom that
  // answers "was this day taken?" without reading the whole column. Keyed by
  // date rather than positional so the header can read it without indexing.
  const footer = useMemo(() => {
    const byDate = new Map<string, DayTally>();
    for (const date of dates) {
      let attended = 0;
      let marked = 0;
      let roster = 0;
      let anyWorkday = false;
      const isFuture = date > today;
      for (const row of rows) {
        if (row.dayMeta.get(date)?.is_workday !== true) continue;
        anyWorkday = true;
        roster += 1;
        const status = row.marks?.get(date)?.status;
        if (status !== undefined) marked += 1;
        if (status === 'present' || status === 'late') attended += 1;
      }
      byDate.set(date, { date, attended, marked, roster, anyWorkday, isFuture });
    }
    return byDate;
  }, [dates, rows, today]);

  const attendedTotal = useMemo(
    () => [...footer.values()].reduce((sum, f) => sum + f.attended, 0),
    [footer],
  );

  const mutation = useMutation<unknown, Error, MutationVars, MutationContext>({
    mutationFn: async ({ batchId, studentId, date, next }) => {
      if (next === null) {
        await clearDailyAttendance(batchId, date, [studentId]);
        return;
      }
      await markDailyAttendance(batchId, date, [{ student_id: studentId, status: next }]);
    },
    onMutate: async ({ batchId, studentId, date, next }) => {
      // The cell knows which batch and month it belongs to, so the optimistic
      // write targets that payload rather than one grid-wide key — a matrix in
      // "All batches" mode spans several caches at once.
      const queryKey = monthKeyFor(batchId, date);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MonthlyAttendanceView>(queryKey);
      setSaveError(null);
      setPendingCells((p) => new Set(p).add(`${batchId}|${studentId}|${date}`));
      if (previous !== undefined) {
        const rest = previous.marks.filter((m) => !(m.student_id === studentId && m.date === date));
        const existing = previous.marks.find((m) => m.student_id === studentId && m.date === date);
        queryClient.setQueryData<MonthlyAttendanceView>(queryKey, {
          ...previous,
          marks:
            next === null
              ? rest
              : [
                  ...rest,
                  { student_id: studentId, date, status: next, notes: existing?.notes ?? null },
                ],
        });
      }
      return { queryKey, previous };
    },
    onError: (err, _v, ctx) => {
      if (ctx !== undefined && ctx.previous !== undefined) {
        queryClient.setQueryData(ctx.queryKey, ctx.previous);
      }
      setSaveError(err.message === '' ? 'Save failed.' : err.message);
    },
    onSettled: (_d, _e, { batchId, studentId, date }) => {
      setPendingCells((p) => {
        const next = new Set(p);
        next.delete(`${batchId}|${studentId}|${date}`);
        return next;
      });
      // Every cached month of this batch, plus the day roster and the rail
      // badges — all three read the row that just changed.
      void queryClient.invalidateQueries({
        queryKey: ['institute', 'batch_monthly_view', batchId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['institute', 'batch_daily_attendance', batchId],
      });
      void queryClient.invalidateQueries({ queryKey: ['institute', 'attendance_summary'] });
    },
  });

  function handleKey(e: KeyboardEvent<HTMLDivElement>): void {
    const { key } = e;
    const isArrow =
      key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';
    if (!isArrow && key !== 'Home' && key !== 'End') return;
    e.preventDefault();
    setFocusKey((prev) => {
      const rMax = rows.length - 1;
      const dMax = dates.length - 1;
      if (rMax < 0 || dMax < 0) return prev;
      let { r: nr, d: nd } = prev;
      if (key === 'ArrowLeft') nd = Math.max(0, nd - 1);
      else if (key === 'ArrowRight') nd = Math.min(dMax, nd + 1);
      else if (key === 'ArrowUp') nr = Math.max(0, nr - 1);
      else if (key === 'ArrowDown') nr = Math.min(rMax, nr + 1);
      else if (key === 'Home') nd = 0;
      else if (key === 'End') nd = dMax;
      const selector = `[data-cell="${nr}-${nd}"]`;
      window.requestAnimationFrame(() => {
        gridRef.current?.querySelector<HTMLButtonElement>(selector)?.focus();
      });
      return { r: nr, d: nd };
    });
  }

  if (rows.length === 0) {
    return (
      <div className={s.empty}>
        <h3 className={s.emptyTitle}>{emptyTitle}</h3>
        <p className={s.emptyBody}>{emptyBody}</p>
      </div>
    );
  }

  // Day columns are `minmax(min, 1fr)`: they never squeeze below the readable
  // minimum, and they absorb the leftover width instead of leaving a dead
  // margin down the right of the card when a week only needs seven of them.
  const colMin = density === 'compact' ? 40 : 52;
  const gridStyle = {
    gridTemplateColumns: `minmax(var(--matrix-name-col), var(--matrix-name-col-max)) repeat(${dates.length}, minmax(${colMin}px, 1fr)) var(--matrix-total-col)`,
  } as const;

  return (
    <>
      {expanded ? (
        <div
          className={s.scrim}
          onClick={() => setExpanded(false)}
          role="presentation"
          aria-hidden="true"
        />
      ) : null}
      <div className={cn(s.frame, expanded ? s.frameFullscreen : null)}>
        {expanded ? (
          <div className={s.toolbar}>
            <div className={s.toolbarTitle}>
              <span className={s.toolbarEyebrow}>{eyebrow}</span>
              <span className={s.toolbarLabel}>{rangeLabel}</span>
            </div>
            <div className={s.toolbarActions}>
              {onPrev !== undefined ? (
                <button
                  type="button"
                  className={s.iconBtn}
                  aria-label="Previous period"
                  onClick={onPrev}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                </button>
              ) : null}
              {onJumpToCurrent !== undefined ? (
                <button
                  type="button"
                  className={cn(s.iconBtn, s.textBtn)}
                  disabled={canJumpToCurrent === false}
                  onClick={onJumpToCurrent}
                >
                  Today
                </button>
              ) : null}
              {onNext !== undefined ? (
                <button
                  type="button"
                  className={s.iconBtn}
                  aria-label="Next period"
                  disabled={nextDisabled === true}
                  onClick={onNext}
                >
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              ) : null}
              <button
                type="button"
                className={s.iconBtn}
                aria-label="Exit fullscreen"
                onClick={() => setExpanded(false)}
              >
                <Minimize2 size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={cn(s.iconBtn, s.expandFab)}
            aria-label="Expand to fullscreen"
            onClick={() => setExpanded(true)}
          >
            <Maximize2 size={16} aria-hidden="true" />
          </button>
        )}

        <div className={s.scroll}>
          <div
            ref={gridRef}
            className={cn(s.grid, density === 'compact' ? s.gridCompact : null)}
            style={gridStyle}
            role="grid"
            tabIndex={-1}
            aria-label={`Attendance for ${rangeLabel}`}
            // Header + student rows + the tally strip along the bottom.
            aria-rowcount={rows.length + 2}
            aria-colcount={dates.length + 2}
            onKeyDown={handleKey}
          >
            {/* ─── Header row ─── */}
            <div className={s.cornerLeft} role="columnheader">
              Student
            </div>
            {dates.map((date, di) => {
              const d = dayjs(date);
              const isToday = date === today;
              // A day is "off" only when it is off for every visible batch —
              // in All-batches mode the batches can disagree.
              const anyWorkday = footer.get(date)?.anyWorkday === true;
              const isFuture = date > today;
              const isMonthStart = d.date() === 1;
              return (
                <div
                  key={date}
                  role="columnheader"
                  className={cn(
                    s.headerCell,
                    !anyWorkday ? s.headerNonWorkday : null,
                    isFuture ? s.headerFuture : null,
                    isToday ? s.headerToday : null,
                    d.day() === 0 ? s.weekStart : null,
                  )}
                  aria-label={`${d.format('dddd, MMMM D, YYYY')}${isFuture ? ' — upcoming' : ''}`}
                  aria-colindex={di + 2}
                >
                  <span className={s.headerDow}>{DOW_LABELS[d.day()]}</span>
                  <span className={s.headerDay}>{d.date()}</span>
                  {isMonthStart ? <span className={s.headerMonth}>{d.format('MMM')}</span> : null}
                </div>
              );
            })}
            <div className={s.cornerRight} role="columnheader">
              Total
            </div>

            {/* ─── Body ─── */}
            {rows.map((row, ri) => {
              const { student, group, dayMeta, marks } = row;
              const totals = totalsFor(dates, dayMeta, marks, today);
              const displayName =
                student.display_name === '' ? student.email : student.display_name;

              return (
                <Fragment key={`${group.batchId}|${student.student_id}`}>
                  {showGroupHeaders && row.firstOfGroup ? (
                    <div className={s.groupBand} role="presentation">
                      <span className={s.groupBandInner}>
                        <span className={s.groupDot} aria-hidden="true" />
                        {group.batchName}
                        <span className={s.groupCount}>
                          {group.students.length} student{group.students.length === 1 ? '' : 's'}
                        </span>
                      </span>
                    </div>
                  ) : null}

                  <div className={s.studentCell} role="rowheader" aria-rowindex={ri + 2}>
                    <span className={s.avatar} aria-hidden="true">
                      {initialsFor(student.display_name, student.email)}
                    </span>
                    <span className={s.studentText}>
                      <span className={s.studentName}>{displayName}</span>
                      <span className={s.studentMeta}>
                        {student.roll_no !== null && student.roll_no !== ''
                          ? `Roll ${student.roll_no} · ${student.email}`
                          : student.email}
                      </span>
                    </span>
                  </div>

                  {dates.map((date, di) => {
                    const meta = dayMeta.get(date);
                    const markInfo = marks?.get(date);
                    const status = markInfo?.status ?? null;
                    // Written by the Phase 135 nightly job, not confirmed by a
                    // person yet — see the `nextStatus` doc comment for what
                    // that changes about the click.
                    const isAutoAbsent = status === 'absent' && markInfo?.autoMarked === true;
                    const isToday = date === today;
                    const isHoliday = meta?.holiday_reason != null && meta.holiday_reason !== '';
                    const isPending = pendingCells.has(
                      `${group.batchId}|${student.student_id}|${date}`,
                    );
                    const isFocused = focusKey.r === ri && focusKey.d === di;
                    const weekStart = dayjs(date).day() === 0 ? s.weekStart : null;

                    // A class that hasn't met yet is not markable. `is_workday`
                    // is mask + holiday only and neither it nor
                    // `mark_daily_attendance` knows about "today", so without
                    // this the week and month views would happily record
                    // attendance for Thursday on a Monday.
                    if (meta?.is_workday !== true || date > today) {
                      const isOff = meta?.is_workday !== true;
                      const reason = isOff
                        ? isHoliday
                          ? `Holiday — ${meta?.holiday_reason ?? ''}`
                          : 'Not a class day'
                        : 'Upcoming — not yet held';
                      // Spelled out rather than repeating the aria-label: a
                      // pointer user hovering an inert cell is asking "why can't
                      // I click this?", and the answer differs per case.
                      const hint = isOff
                        ? isHoliday
                          ? `${dayjs(date).format('ddd D MMM')} · holiday — ${meta?.holiday_reason ?? ''}`
                          : `${dayjs(date).format('dddd')}s are not a class day for this batch`
                        : `${dayjs(date).format('ddd D MMM')} hasn’t been held yet — nothing to mark`;
                      return (
                        <div
                          key={date}
                          role="gridcell"
                          aria-colindex={di + 2}
                          aria-label={`${displayName}, ${dayjs(date).format('MMMM D')}, ${reason}`}
                          title={hint}
                          className={cn(
                            s.slot,
                            s.slotInert,
                            isOff ? (isHoliday ? s.slotHoliday : s.slotOff) : s.slotFuture,
                            weekStart,
                          )}
                        />
                      );
                    }

                    const statusLabel =
                      status === null
                        ? 'Unmarked'
                        : isAutoAbsent
                          ? 'Absent (auto)'
                          : // eslint-disable-next-line security/detect-object-injection -- enum key
                            STATUS_LABEL[status];
                    const upcoming = nextStatus(status, isAutoAbsent);

                    return (
                      <div
                        key={date}
                        className={cn(s.slot, isToday ? s.slotToday : null, weekStart)}
                        role="presentation"
                      >
                        <button
                          type="button"
                          role="gridcell"
                          data-cell={`${ri}-${di}`}
                          aria-colindex={di + 2}
                          aria-label={`${displayName}, ${dayjs(date).format('dddd MMMM D')}, ${statusLabel}${
                            isAutoAbsent ? ' — nobody marked this day, click to correct' : ''
                          }`}
                          title={
                            isAutoAbsent
                              ? 'Absent — auto-marked, nobody recorded this day. Click to set present.'
                              : `${statusLabel} — click to set ${
                                  upcoming === null
                                    ? 'unmarked'
                                    : // eslint-disable-next-line security/detect-object-injection -- enum key
                                      STATUS_LABEL[upcoming].toLowerCase()
                                }`
                          }
                          tabIndex={isFocused ? 0 : -1}
                          disabled={isPending}
                          onClick={() => {
                            setFocusKey({ r: ri, d: di });
                            mutation.mutate({
                              batchId: group.batchId,
                              studentId: student.student_id,
                              date,
                              next: upcoming,
                            });
                          }}
                          onFocus={() => setFocusKey({ r: ri, d: di })}
                          className={cn(
                            s.chip,
                            status === null
                              ? s.chipUnmarked
                              : isAutoAbsent
                                ? s.chipAbsentAuto
                                : // eslint-disable-next-line security/detect-object-injection -- enum key
                                  CHIP_CLASS[status],
                            isPending ? s.chipPending : null,
                          )}
                        >
                          {status === null
                            ? ''
                            : // eslint-disable-next-line security/detect-object-injection -- enum key
                              STATUS_GLYPH[status]}
                        </button>
                      </div>
                    );
                  })}

                  <div
                    className={s.totalCell}
                    role="gridcell"
                    aria-rowindex={ri + 2}
                    aria-label={`${totals.pct} percent attended, ${totals.attended} of ${totals.workdays} class days`}
                  >
                    <ProgressRing pct={totals.pct} />
                    <span className={s.totalText}>
                      <span className={s.totalPercent}>{totals.pct}%</span>
                      <span className={s.totalFraction}>
                        {totals.attended}/{totals.workdays}
                      </span>
                    </span>
                  </div>
                </Fragment>
              );
            })}

            {/* ─── Footer tally ─── */}
            <div className={cn(s.cornerLeft, s.footerCorner)} role="rowheader">
              Present
            </div>
            {[...footer.values()].map((f) => (
              <div
                key={f.date}
                role="gridcell"
                className={cn(
                  s.footerCell,
                  f.date === today ? s.footerToday : null,
                  dayjs(f.date).day() === 0 ? s.weekStart : null,
                )}
                title={
                  f.isFuture
                    ? 'Upcoming'
                    : f.anyWorkday
                      ? `${f.attended} attended · ${f.marked}/${f.roster} marked`
                      : 'Not a class day'
                }
                aria-label={`${dayjs(f.date).format('MMMM D')}: ${
                  f.isFuture
                    ? 'upcoming'
                    : f.anyWorkday
                      ? `${f.attended} attended of ${f.roster}`
                      : 'not a class day'
                }`}
              >
                {f.anyWorkday && !f.isFuture ? f.attended : '·'}
              </div>
            ))}
            <div
              className={cn(s.cornerRight, s.footerCorner)}
              role="gridcell"
              aria-label={`${attendedTotal} attended across ${dates.length === 1 ? 'this day' : 'this period'}`}
            >
              <span className={s.footerTotal}>{attendedTotal}</span>
            </div>
          </div>
        </div>

        <div className={s.footerBar}>
          <div className={s.legend} aria-label="Legend">
            <span className={s.legendItem}>
              <span className={cn(s.legendSwatch, s.legendSwatchPresent)} aria-hidden="true" />
              Present
            </span>
            <span className={s.legendItem}>
              <span className={cn(s.legendSwatch, s.legendSwatchAbsent)} aria-hidden="true" />
              Absent
            </span>
            <span className={s.legendItem}>
              <span className={cn(s.legendSwatch, s.legendSwatchAbsentAuto)} aria-hidden="true" />
              Absent (auto — unmarked)
            </span>
            <span className={s.legendItem}>
              <span className={cn(s.legendSwatch, s.legendSwatchLate)} aria-hidden="true" />
              Late
            </span>
            <span className={s.legendItem}>
              <span className={cn(s.legendSwatch, s.legendSwatchExcused)} aria-hidden="true" />
              Excused
            </span>
            <span className={s.legendItem}>
              <span className={cn(s.legendSwatch, s.legendSwatchOff)} aria-hidden="true" />
              Off / holiday
            </span>
            <span className={s.legendItem}>
              <span className={cn(s.legendSwatch, s.legendSwatchFuture)} aria-hidden="true" />
              Upcoming
            </span>
            <span className={s.legendHint}>
              Click cycles P → A → L → E → blank — an auto-absent cell jumps straight to P.
            </span>
          </div>
          <p className={s.saveError} role="status" aria-live="polite">
            {saveError}
          </p>
        </div>
      </div>
    </>
  );
}

interface ProgressRingProps {
  pct: number;
}

function ProgressRing({ pct }: ProgressRingProps): JSX.Element {
  const r = 14;
  const c = 2 * Math.PI * r;
  const dash = Math.max(0, Math.min(100, pct)) / 100;
  const offset = c * (1 - dash);
  return (
    <span className={s.ringWrap} aria-hidden="true">
      <svg className={s.ringSvg} viewBox="0 0 36 36">
        <circle className={s.ringTrack} cx="18" cy="18" r={r} />
        <circle
          className={cn(s.ringFill, ringStrokeClass(pct))}
          cx="18"
          cy="18"
          r={r}
          strokeDasharray={`${c} ${c}`}
          strokeDashoffset={offset}
        />
      </svg>
      <span className={s.ringLabel}>{pct}</span>
    </span>
  );
}
